import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import Text from '@cartwhl/ui/base/TextNew';

import type { AssetLibraryGridViewState } from '../assetLibraryGridViewState';
import type { AssetLibraryViewMode } from '../assetLibraryGridLayout';
import type { MotionLibraryDisplayItem } from '../assetLibraryMotionLibrary';
import { ASSET_LIBRARY_LOAD_MORE_ROOT_MARGIN } from '../assetLibraryGridLayout';
import { getMotionLibraryGridItemKey } from '../assetLibraryMotionLibrary';
import type { AssetLibraryDisplayItem } from '../utils';
import { getAssetLibraryItemKey } from '../utils';
import AssetLibraryCard from './AssetLibraryCard';
import AssetLibraryLoading from './AssetLibraryLoading';
import AssetLibraryMotionCard from './AssetLibraryMotionCard';
import AssetLibraryMotionSupplementDivider from './AssetLibraryMotionSupplementDivider';
import AssetLibraryNoResultsMessage from './AssetLibraryNoResultsMessage';

const CARD_ENTRY_DELAY_MS = 150;
const CARD_STAGGER_MS = 35;

function getAssetCardAnimationDelay(index: number) {
  return `${CARD_ENTRY_DELAY_MS + Math.min(index, 11) * CARD_STAGGER_MS}ms`;
}

export interface AssetLibraryAssetsGridProps {
  embedded: boolean;
  gridClassName: string;
  cardLayout: AssetLibraryViewMode;
  skeletonCount: number;
  gridView: AssetLibraryGridViewState;
  displayAssets: AssetLibraryDisplayItem[];
  motionLibraryItems: MotionLibraryDisplayItem[];
  motionLibraryError?: string;
  scrollRoot: HTMLDivElement | null;
  getAssetClickHandler: (
    asset: AssetLibraryDisplayItem,
  ) => (() => void) | undefined;
  bulkSelectionEnabled?: boolean;
  isAssetSelected?: (asset: AssetLibraryDisplayItem) => boolean;
  onToggleAssetSelection?: (asset: AssetLibraryDisplayItem) => void;
  onStockMotionClick: (motion: MotionLibraryDisplayItem) => void;
  onLoadMore: () => void;
}

export default function AssetLibraryAssetsGrid({
  embedded,
  gridClassName,
  cardLayout,
  skeletonCount,
  gridView,
  displayAssets,
  motionLibraryItems,
  motionLibraryError,
  scrollRoot,
  getAssetClickHandler,
  bulkSelectionEnabled = false,
  isAssetSelected,
  onToggleAssetSelection,
  onStockMotionClick,
  onLoadMore,
}: AssetLibraryAssetsGridProps) {
  const {
    isFallbackMode,
    isSupplementMode,
    isMotionLibraryLoading,
    showNoResultsMotionLibrary,
    showSupplementDivider,
    showLoadMore,
    doneLoadingAll,
    isLoadingMoreContent,
  } = gridView;

  return (
    <>
      {showNoResultsMotionLibrary ? (
        <div className="flex flex-col gap-6">
          <div className="pt-8">
            <AssetLibraryNoResultsMessage />
          </div>
          <AssetLibraryMotionSupplementDivider />
          {isMotionLibraryLoading ? (
            <AssetLibraryLoading
              count={skeletonCount}
              gridClassName={gridClassName}
              thumbnailAspectClass="aspect-square"
            />
          ) : null}
          {motionLibraryError && motionLibraryItems.length === 0 ? (
            <Text
              token="14/medium"
              className="text-secondary text-center text-pretty"
            >
              {motionLibraryError}
            </Text>
          ) : null}
        </div>
      ) : null}
      {motionLibraryItems.length > 0 || !isFallbackMode ? (
        <ul className={gridClassName}>
          {!isFallbackMode
            ? displayAssets.map((asset, index) => (
                <li
                  key={getAssetLibraryItemKey(asset)}
                  className="asset-library-card-enter"
                  style={{ animationDelay: getAssetCardAnimationDelay(index) }}
                >
                  <AssetLibraryCard
                    asset={asset}
                    layout={cardLayout}
                    bulkSelectionEnabled={bulkSelectionEnabled}
                    onClick={getAssetClickHandler(asset)}
                    onToggleSelection={
                      onToggleAssetSelection
                        ? () => onToggleAssetSelection(asset)
                        : undefined
                    }
                    selected={isAssetSelected?.(asset) ?? false}
                  />
                </li>
              ))
            : null}
          {showSupplementDivider ? (
            <AssetLibraryMotionSupplementDivider />
          ) : null}
          {(isFallbackMode || isSupplementMode) &&
            motionLibraryItems.map((motion, index) => (
              <li
                key={getMotionLibraryGridItemKey(motion)}
                className="asset-library-card-enter"
                style={{
                  animationDelay: getAssetCardAnimationDelay(
                    displayAssets.length + index,
                  ),
                }}
              >
                <AssetLibraryMotionCard
                  motion={motion}
                  layout={cardLayout}
                  onClick={() => onStockMotionClick(motion)}
                />
              </li>
            ))}
        </ul>
      ) : null}
      {showLoadMore ? (
        <div className="flex justify-center py-6">
          <InfiniteLoadingTrigger
            loading={isLoadingMoreContent}
            done={doneLoadingAll}
            onEnter={onLoadMore}
            root={embedded ? scrollRoot : undefined}
            rootMargin={ASSET_LIBRARY_LOAD_MORE_ROOT_MARGIN}
          />
        </div>
      ) : null}
    </>
  );
}
